import { buildRpaDomainSeoBundle } from "../rpaSeoUtils";

export const REAL_ESTATE_DOMAIN_INTRO = {
  title: "Real Estate & Construction",
  content: "Document AI + RPA and workflow automation across Construction, Real Estate Leasing. Each use case includes challenge and solution detail, process diagrams, and human-in-the-loop review for enterprises in India, UAE, KSA, UK, and USA.",
  seoDescription: "Real Estate & Construction automation use cases — document AI + RPA and workflow automation across Construction, Real Estate Leasing. Validation, approvals, system updates, and exception handling.",
};

const slugify = (label) =>
  label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

function withMeta(uc) {
  return {
    ...uc,
    groupId: slugify(uc.subIndustry),
    seoTitle: uc.title,
    title: uc.shortTitle || uc.title,
    systems: uc.systems || SYSTEMS,
  };
}

const SYSTEMS = "Project Management, ERP, Document Management, CRM, Email, Power BI";

const RAW = [
  {
    id: "ra-bill-and-invoice-verification",
    subIndustry: "Construction",
    department: "Billing",
    businessProcess: "RA bill and invoice verification",
    regions: "India, UAE/Dubai, KSA/Saudi Arabia, UK, USA",
    shortTitle: "RA bill and invoice verification",
    title: "Ra Bill And Invoice Verification Automation for Real Estate & Construction",
    seoKeywords: "real estate & construction, ra bill and invoice verification, AI automation, RPA, agentic AI, document AI",
    challenge: "Running account bills are manually matched with measurements, POs and contract terms.",
    solution: "Implement Document AI + RPA to capture inputs, validate rules, route exceptions, generate summaries or actions, and update the target systems with human approval where required.",
    automationType: "Document AI + RPA",
    buyerPersona: "CFO, Commercial Head",
    systems: "ERP, Project Management, DMS, Email",
    agentsRequired: "Document Extraction Agent, RA Validation Agent, Exception Agent",
    inputData: "Forms, PDFs, emails, scanned documents, attachments, master data and ra bill and invoice verification rules",
    outputActions: "Extracted structured data, validation results, exception list and approval-ready ra bill and invoice verification record",
    keyBenefits: "Faster Closure, Fewer Reconciliation Errors, Reduced Manual Effort And Better Finance Visibility",
    roiTimeline: "6-12 months",
    implementationComplexity: "High",
    businessImpact: "High",
  },
  {
    id: "purchase-requisition-to-po-automation",
    subIndustry: "Construction",
    department: "Procurement",
    businessProcess: "Purchase requisition to PO automation",
    regions: "India, UAE/Dubai, KSA/Saudi Arabia, UK, USA",
    shortTitle: "Purchase requisition to PO automation",
    title: "Purchase Requisition To Po Automation Automation for Real Estate & Construction",
    seoKeywords: "real estate & construction, purchase requisition to po automation, AI automation, RPA, agentic AI",
    challenge: "Purchase requests, vendor quotes and approvals are managed manually.",
    solution: "Implement RPA + Workflow Automation to capture inputs, validate rules, route exceptions, generate summaries or actions, and update the target systems with human approval where required.",
    automationType: "RPA + Workflow Automation",
    buyerPersona: "Procurement Head",
    systems: "ERP, Vendor Portal, Email, Procurement System",
    agentsRequired: "RPA Posting Agent, Purchase Validation Agent, Exception Agent",
    inputData: "Emails, forms, system records, task notes, master data and purchase requisition to po automation process rules",
    outputActions: "Validated workflow record, task assignment, status update and exception report for purchase requisition to po automation",
    keyBenefits: "Faster Turnaround, Lower Manual Effort, Improved Accuracy And Better Operational Visibility",
    roiTimeline: "3-6 months",
    implementationComplexity: "Medium",
    businessImpact: "High",
  },
  {
    id: "material-delivery-reconciliation",
    subIndustry: "Construction",
    department: "Materials",
    businessProcess: "Material delivery reconciliation",
    regions: "India, UAE/Dubai, KSA/Saudi Arabia, UK, USA",
    shortTitle: "Material delivery reconciliation",
    title: "Material Delivery Reconciliation Automation for Real Estate & Construction",
    seoKeywords: "real estate & construction, material delivery reconciliation, AI automation, RPA, agentic AI, document AI",
    challenge: "Site teams manually match delivery notes, gate entries and purchase orders.",
    solution: "Implement Document AI + RPA to capture inputs, validate rules, route exceptions, generate summaries or actions, and update the target systems with human approval where required.",
    automationType: "Document AI + RPA",
    buyerPersona: "Materials Head, Project Head",
    systems: "ERP, WMS, Gate Entry System, Email",
    agentsRequired: "Document Extraction Agent, Material Validation Agent, Exception Agent",
    inputData: "Forms, PDFs, emails, scanned documents, attachments, master data and material delivery reconciliation rules",
    outputActions: "Extracted structured data, validation results, exception list and approval-ready material delivery reconciliation record",
    keyBenefits: "Faster Turnaround, Lower Manual Effort, Improved Accuracy And Better Operational Visibility",
    roiTimeline: "3-6 months",
    implementationComplexity: "Medium",
    businessImpact: "High",
  },
  {
    id: "tenant-onboarding-and-lease-abstraction",
    subIndustry: "Real Estate Leasing",
    department: "Leasing",
    businessProcess: "Tenant onboarding and lease abstraction",
    regions: "India, UAE/Dubai, KSA/Saudi Arabia, UK, USA",
    shortTitle: "Tenant onboarding and lease abstraction",
    title: "Tenant Onboarding And Lease Abstraction Automation for Real Estate & Construction",
    seoKeywords: "real estate & construction, tenant onboarding and lease abstraction, AI automation, RPA, agentic AI, document AI",
    challenge: "Leases, IDs, KYC and deposit details are processed manually.",
    solution: "Implement Document AI + RPA to capture inputs, validate rules, route exceptions, generate summaries or actions, and update the target systems with human approval where required.",
    automationType: "Document AI + RPA",
    buyerPersona: "Leasing Head, CFO",
    systems: "CRM, Lease Management, ERP, DMS",
    agentsRequired: "Document Extraction Agent, Tenant Validation Agent, Exception Agent",
    inputData: "Forms, PDFs, emails, scanned documents, attachments, master data and tenant onboarding and lease abstraction rules",
    outputActions: "Extracted structured data, validation results, exception list and approval-ready tenant onboarding and lease abstraction record",
    keyBenefits: "Faster Turnaround, Lower Manual Effort, Improved Accuracy And Better Operational Visibility",
    roiTimeline: "3-6 months",
    implementationComplexity: "Medium",
    businessImpact: "High",
  },
];
export const REAL_ESTATE_USE_CASES = RAW.map(withMeta);
const GROUP_ORDER = ["Construction", "Real Estate Leasing"];
export const REAL_ESTATE_GROUPS = GROUP_ORDER.map((label) => ({
  id: slugify(label),
  label,
})).filter((g) => REAL_ESTATE_USE_CASES.some((uc) => uc.groupId === g.id));
const seo = buildRpaDomainSeoBundle({
  domainSlug: 'real-estate-construction-domain',
  domainIntro: REAL_ESTATE_DOMAIN_INTRO,
  useCases: REAL_ESTATE_USE_CASES,
});
export const REAL_ESTATE_USE_CASES_SEO = seo.useCasesSeo;
export const REAL_ESTATE_DOMAIN_FAQS = seo.domainFaqs;
export const REAL_ESTATE_ITEM_LIST_JSONLD = seo.itemListJsonLd;
export const REAL_ESTATE_FAQ_JSONLD = seo.faqJsonLd;
export const REAL_ESTATE_BREADCRUMB_JSONLD = seo.breadcrumbJsonLd;
export const REAL_ESTATE_WEBPAGE_JSONLD = seo.webPageJsonLd;

